import { KeyNames } from "../constants/keyNames";
import { assertNever } from "./typeGuards/assertNever";

/**
 * Formats a number for the provided locale with the specified number of decimal places and
 * with optional thousands separators.
 *
 * @param value - The number to format.
 * @param locale - The locale to use for formatting.
 * @param options - The options to use for formatting.
 */
export function formatNumber(
  value: number,
  locale: string,
  options: { decimalPlaces?: number; disallowGroupings?: boolean } = {},
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: options.decimalPlaces,
    maximumFractionDigits: options.decimalPlaces,
    useGrouping: !options.disallowGroupings,
  }).format(value);
}

/**
 * Parse a number from a string.
 *
 * @param value - The string to parse.
 * @param locale - The locale to use for parsing.
 * @returns The parsed number.
 */
export function i18nParseNumber(value: string, locale: string) {
  const example = Intl.NumberFormat(locale, { useGrouping: true }).format(1111.1);
  // Remove spaces, thousand separators and replace decimal separator with a dot.
  return Number(value.replace(/\s/g, "").replaceAll(example.charAt(1), "").replaceAll(example.charAt(5), "."));
}

/**
 * Parse a number from a string or number.
 *
 * @param value - The string or number to parse. Null values are handled gracefully.
 * @param locale - The locale to use for parsing.
 * @returns The parsed number, or null if the value is null or cannot be parsed.
 */
export function i18nParseToNumberOrNull(value: string | number | null, locale: string): number | null {
  if (value == null) {
    return null;
  }

  switch (typeof value) {
    case "number":
      return Number.isFinite(value) ? value : null;
    case "string": {
      // (jullman): Empty strings are coerced to 0 by i18nParseNumber, so we need to check this
      // explicitly.
      if (value === "") {
        return null;
      }
      const resolvedValue = i18nParseNumber(value, locale);
      if (resolvedValue != null && Number.isFinite(resolvedValue)) {
        return resolvedValue;
      }
      return null;
    }
    default:
      assertNever(value);
  }
}

/**
 * Get the locale's decimal separator.
 */
function i18nGetDecimalSeparator(locale: string) {
  const localDecimal = Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((part) => part.type === "decimal");

  // In unexpected case, fall back to the en-us separator.
  return localDecimal?.value ?? ".";
}

/**
 * Check if a string is a valid prefix to a number in the given locale and with the given
 * formatting. Valid prefixes are non-numeric strings such as "-", ".", and "-.".
 *
 * @param value - The string to check.
 * @param locale - The locale to use for parsing.
 * @param maximumFractionDigits - The maximum number of decimal places to support. Defaults to 0.
 * @returns True if the string is a valid numeric prefix, false otherwise.
 */
export function i18nIsNumericPrefixString(value: string, locale: string, maximumFractionDigits = 0) {
  const isDecimalSupported = maximumFractionDigits > 0;
  const localeDecimalSeparator = i18nGetDecimalSeparator(locale);

  switch (value) {
    case localeDecimalSeparator:
      return isDecimalSupported;
    case KeyNames.MINUS:
      return true;
    case `${KeyNames.MINUS}${localeDecimalSeparator}`:
      return isDecimalSupported;
    default:
      return false;
  }
}
